<template>
  <div id="app" class="app-container">
    <header class="app-header">
      <div class="header-content">
        <div class="header-brand">
          <div class="brand-icon">
            <i class="pi pi-qrcode"></i>
          </div>
          <div class="brand-text">
            <h1 class="brand-title">RapID Audit</h1>
          </div>
        </div>
        <div class="header-right">
          <div v-if="isAuthenticated" class="user-menu-header">
            <div class="user-info">
              <div class="user-avatar">
                <span class="user-initial">{{ userInitial }}</span>
              </div>
              <div class="user-details">
                <span class="user-name">{{ userName }}</span>
              </div>
            </div>
            <LogoutButton />
          </div>
        </div>
      </div>
    </header>

    <main class="app-main">
      <RouterView v-slot="{ Component, route }">
        <component
          :is="Component"
          v-if="route.name === 'oauth-callback' || route.matched.length > 0"
        />
        <OAuthCallback v-else-if="route.path === '/auth/callback'" />
      </RouterView>
    </main>

    <footer class="app-footer">
      <div class="footer-content">
        <p class="footer-text">&copy; 2025 RapID Audit. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouterView } from 'vue-router';
import LogoutButton from './components/auth/LogoutButton.vue';
import OAuthCallback from './views/auth/OAuthCallback.vue';
import { useAuthStore } from './stores/auth.store';
import { storeToRefs } from 'pinia';

const authStore = useAuthStore();
const { isAuthenticated, user } = storeToRefs(authStore);
const userName = computed(
  () => user.value?.name || user.value?.username || user.value?.email || 'User',
);
const userInitial = computed(() => {
  const name = userName.value;
  return name.charAt(0).toUpperCase();
});
</script>

<style scoped>
.app-container {
  height: 100vh;
  background: linear-gradient(135deg, #f6f8fb 0%, #e5ebf1 100%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.app-header {
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-brand {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.brand-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.125rem;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  flex-shrink: 0;
}

.brand-text {
  display: flex;
  flex-direction: column;
}

.brand-title {
  font-size: 1.25rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  line-height: 1.2;
}

.brand-subtitle {
  font-size: 0.75rem;
  color: #718096;
  margin: 0;
  margin-top: 0.125rem;
  display: none;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-direction: row;
}

.user-menu-header {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  background: rgba(102, 126, 234, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.user-name {
  font-weight: 600;
  font-size: 0.8125rem;
  color: #1a202c;
  line-height: 1.2;
}

.app-main {
  flex: 1 1 0;
  width: 100%;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.app-footer {
  background: white;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  margin-top: auto;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
}

.footer-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0.75rem 1rem;
  text-align: center;
}

.footer-text {
  color: #718096;
  font-size: 0.75rem;
  margin: 0;
}

.footer-api {
  color: #9ca3af;
  font-size: 0.6875rem;
  margin: 0.25rem 0 0 0;
  display: none;
}

@media (max-width: 768px) {
  .header-content {
    padding: 0.5rem 0.75rem;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
  }

  .brand-title {
    font-size: 1.125rem;
  }

  .brand-icon {
    width: 32px;
    height: 32px;
    font-size: 1rem;
  }

  .header-right {
    flex-direction: row;
    gap: 0.375rem;
    flex-shrink: 0;
  }

  .user-info {
    padding: 0.25rem 0.5rem;
    gap: 0.375rem;
  }

  .user-avatar {
    width: 28px;
    height: 28px;
    font-size: 0.75rem;
  }

  .user-name {
    font-size: 0.75rem;
  }

  .app-main {
    padding: 0.5rem;
  }

  .app-footer {
    display: none;
  }

  .footer-content {
    padding: 0.5rem 0.75rem;
  }

  .footer-text {
    font-size: 0.6875rem;
  }
}
</style>
